import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useGetCommonStore } from '@/store';

import { basisPointsToDisplay } from '../api/loyalty-sse';
import {
  calculateRetainPercentage,
  formatTimeRemaining,
  getWarningThresholdDays,
  MS_PER_DAY,
  MS_PER_SECOND,
} from '../helpers';
import { FIRST_LEVEL_INDEX } from '../types';

const LVL_FALLBACK = 1;

// ============ Types ============

export interface LoyaltyProgressData {
  /** Current LP points (display value) */
  points: number;
  /** Target amount to reach next level or retain current (display value) */
  targetAmount: number;
  /** Points needed to upgrade to next level (display value) */
  upgradeAmount: number;
  /** Points needed to retain current level (display value) */
  retainAmount: number;
  /** LP remaining to secure current level */
  lpToSecure: number;
  /** Whether user has secured their current level */
  isSecured: boolean;
  /** Whether user is on level 1 */
  isFirstLevel: boolean;
  /** Whether user is on the maximum level */
  isLastLevel: boolean;
  /** Whether to show retention goal UI (levels > 1) */
  showRetentionGoal: boolean;
  /** Percentage position for retention milestone marker (0-100) */
  retainPercentage: number;
  /** Retention deadline as Date object */
  retentionDeadline: Date | null;
  /** Formatted deadline string (e.g., "Mar 15") */
  formattedDeadline: string | null;
  /** Formatted time remaining string (e.g., "02d:05h:30m") */
  timeRemaining: string | null;
  /** Time remaining in seconds for countdown components */
  timeLeftSeconds: number;
  /** Whether to show the LP warning (not secured + deadline approaching) */
  isLPWarningVisible: boolean;
}

// ============ Hook ============

export const useLoyaltyProgress = (): LoyaltyProgressData => {
  const {
    i18n: { language },
  } = useTranslation();

  const useCommonStore = useGetCommonStore();
  const loyaltySseState = useCommonStore.use.loyaltySseState();

  // Derive values from loyaltySseState (reactive subscription)
  const points = loyaltySseState
    ? Math.floor(basisPointsToDisplay(loyaltySseState.points))
    : 0;
  const level = loyaltySseState?.level ?? null;
  const levels = loyaltySseState?.levels ?? [];
  const retentionDeadline = loyaltySseState?.retentionDeadline
    ? new Date(loyaltySseState.retentionDeadline)
    : null;

  return useMemo(() => {
    const currentLevel = level ? levels[level.number - 1] : undefined;
    const upgradeAmount = basisPointsToDisplay(
      currentLevel?.upgradePointAmount ?? 0
    );
    const retainAmount = basisPointsToDisplay(
      currentLevel?.retainPointAmount ?? 0
    );

    const isFirstLevel = level?.id === FIRST_LEVEL_INDEX;
    const isLastLevel = level?.id === levels.length;

    // For last level, target is retention goal; otherwise upgrade goal
    const targetAmount = isLastLevel
      ? retainAmount
      : upgradeAmount || retainAmount;

    const lpToSecure = Math.max(0, retainAmount - points);
    const isSecured = lpToSecure === 0;
    const retainPercentage = calculateRetainPercentage(
      isLastLevel,
      retainAmount,
      targetAmount
    );

    // Show retention info only for levels > 1
    const showRetentionGoal = !isFirstLevel && retainAmount > 0;

    const formattedDeadline = retentionDeadline
      ? retentionDeadline.toLocaleDateString(language, {
          month: 'short',
          day: 'numeric',
        })
      : null;

    const timeDiff = retentionDeadline
      ? retentionDeadline.getTime() - Date.now()
      : 0;

    const timeRemaining = formatTimeRemaining(timeDiff);
    const timeLeftSeconds = Math.max(0, Math.floor(timeDiff / MS_PER_SECOND));

    const warningThresholdSeconds =
      (getWarningThresholdDays(level?.id ?? LVL_FALLBACK) * MS_PER_DAY) /
      MS_PER_SECOND;
    const isLPWarningVisible =
      lpToSecure > 0 &&
      formattedDeadline !== null &&
      timeLeftSeconds < warningThresholdSeconds;

    return {
      points,
      targetAmount,
      upgradeAmount,
      retainAmount,
      lpToSecure,
      isSecured,
      isFirstLevel,
      isLastLevel,
      showRetentionGoal,
      retainPercentage,
      retentionDeadline,
      formattedDeadline,
      timeRemaining,
      timeLeftSeconds,
      isLPWarningVisible,
    };
  }, [levels, level, points, retentionDeadline, language]);
};
